import type { TermId } from '../../glossary'
import { Term } from '../Term'

/**
 * The small pieces every panel of the Model page is built from.
 *
 * Extracted when the page grew a third panel and the file passed seven hundred lines. Nothing here
 * is new; what is new is that `Fact`, `Metric` and the two date formatters now have one definition
 * instead of being copied into whichever panel needed them next — the same argument as `markets.ts`
 * and `Data/TeamNames.cs`, one scale smaller.
 */

/**
 * A figure and the sentence that says what it means.
 *
 * The number stays large, because the number is the object — but it is not left to speak for itself
 * under a three-word label. "Calibration error · 8.0 pts · mean gap between stated and actual" asks
 * the reader to already know what calibration error is and why a gap between stated and actual
 * matters more than being right.
 */
export function Headline({
  figure,
  sentence,
  note,
  term,
}: {
  figure: string
  sentence: React.ReactNode
  note: React.ReactNode
  /** Optional glossary word to teach here, on the page that uses it most and taught it least. */
  term?: TermId
}) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="tabular text-2xl text-ink">{figure}</div>
      <p className="mt-1 text-sm text-ink-soft">{sentence}</p>
      <p className="mt-2 text-xs text-ink-faint">
        {note}
        {term ? (
          <>
            {' · '}
            <Term id={term} />
          </>
        ) : null}
      </p>
    </div>
  )
}

/** A labelled value inside a definition list. */
export function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 sm:justify-start">
      <dt className="text-ink-faint">{label}</dt>
      <dd className="tabular text-ink-soft">{value}</dd>
    </div>
  )
}

/** A labelled value inline, for a row of them under a rule. */
export function Metric({ label, value }: { label: string; value: string }) {
  return (
    <span className="whitespace-nowrap">
      <span className="text-ink-faint">{label} </span>
      <span className="tabular text-ink">{value}</span>
    </span>
  )
}

/** A section heading, at the one size every panel uses. */
export function PanelHeading({
  children,
  lead,
}: {
  children: React.ReactNode
  lead?: React.ReactNode
}) {
  return (
    <>
      <h3 className="text-sm font-medium text-ink">{children}</h3>
      {lead ? <p className="mt-1 max-w-3xl text-sm text-ink-soft">{lead}</p> : null}
    </>
  )
}
